import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SeriesDto } from '~/backend/connector-next-api/connector-next-schemas';
import { seriesKey } from '~/backend/connector-next-api/seriesKey';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Separated } from '~/components/Separated';
import {
    formatDay,
    formatDayText,
    seriesDisplayName,
    type SeriesDayGroup,
    UNKNOWN_DAY,
} from '../model';
import DatasetRow, {
    type DatasetInfoOffer,
    type DatasetPrimary,
} from './DatasetRow';

/** Dataset details on demand, with the key of the row showing them and of the row pinning them. */
type DatasetInfoProps = {
    info?: DatasetInfoOffer;
    infoOpenKey?: string;
    infoPinnedKey?: string;
};

/**
 * Row modes: `patient` (the patient panel: designations and case ticks),
 * `select` (the import review, where choosing what to import is the task),
 * `imported` (sent series), `readOnly` (plain listing). Within a selection,
 * `alreadyStored` rows the record already holds are decided per row.
 */
type DatasetDayGroupProps = (
    | {
          group: SeriesDayGroup;
          mode: 'patient';
          primary?: DatasetPrimary;
          /** Case ticks: the ticked dataset ids and how to tick one. */
          ticked?: ReadonlySet<string>;
          onTick?: (datasetId: string) => void;
      }
    | {
          group: SeriesDayGroup;
          mode?: 'select';
          checked: ReadonlySet<string>;
          onToggle: (seriesId: string) => void;
          alreadyStored?: ReadonlySet<string>;
      }
    | {
          group: SeriesDayGroup;
          mode: 'imported' | 'readOnly';
      }
) &
    DatasetInfoProps;

/**
 * One day of the dataset timeline: a sticky bubble on the left rail with the
 * day's label, modalities held and count, then the day's dataset rows.
 */
export default function DatasetDayGroup(props: DatasetDayGroupProps) {
    const { group, info, infoOpenKey, infoPinnedKey } = props;
    const intl = useIntl();
    const selection = 'checked' in props ? props : undefined;
    const patientRows = props.mode === 'patient' ? props : undefined;
    return (
        <Box>
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    height: 40,
                    px: 3,
                    fontSize: 13,
                    color: 'text.secondary',
                    bgcolor: 'background.raised',
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        position: 'relative',
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'center',
                        // The rail runs on through the header, behind the bubble.
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: BUBBLE_SIZE / 2 - RAIL_WIDTH / 2,
                            top: 0,
                            bottom: 0,
                            width: RAIL_WIDTH,
                            bgcolor: RAIL_COLOR,
                        },
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: BUBBLE_SIZE,
                            height: BUBBLE_SIZE,
                            flexShrink: 0,
                            borderRadius: '50%',
                            border: 2,
                            borderColor:
                                group.day === UNKNOWN_DAY
                                    ? 'divider'
                                    : 'primary.main',
                            bgcolor: 'background.raised',
                        }}
                    />
                </Box>
                <Box
                    component="span"
                    sx={{
                        fontFamily: 'fontFamilyMonospace',
                        fontWeight: 500,
                        color: 'text.primary',
                    }}
                >
                    {group.label}
                </Box>
                <Separated>
                    {group.modalities.map((modality) => (
                        <Box
                            key={modality}
                            component="span"
                            sx={{ letterSpacing: '0.04em' }}
                        >
                            {modality}
                        </Box>
                    ))}
                </Separated>
                <Box
                    component="span"
                    sx={{
                        fontFamily: 'fontFamilyMonospace',
                        ml: 'auto',
                    }}
                >
                    <FormattedMessage
                        id="patientList.drilldown.datasetCount"
                        values={{ count: group.seriesCount }}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    ml: `${RAIL_LEFT}px`,
                    borderLeft: RAIL_WIDTH,
                    borderColor: RAIL_COLOR,
                }}
            >
                {group.nodes.map((node, index) => (
                    <Box key={rowKey(node.series, group.day, index)}>
                        {node.referencedImageMissing && (
                            // Never the referenced Series Instance UID: a DICOM UID
                            // is PHI, and what the reader needs is that the image
                            // is not here, not which one it was.
                            <Typography
                                sx={{
                                    pl: 6,
                                    pt: 0.5,
                                    fontSize: 10,
                                    color: 'action.disabled',
                                }}
                            >
                                <FormattedMessage id="patientList.drilldown.imageNotHere" />
                            </Typography>
                        )}
                        {!node.seriesHidden &&
                            row(node.series, group.day, index)}
                        {node.structureSets.length > 0 && (
                            <Box sx={{ pl: 3 }}>
                                <Typography
                                    sx={{
                                        pl: 3,
                                        pt: 0.5,
                                        fontSize: 10,
                                        color: 'action.disabled',
                                    }}
                                >
                                    {node.seriesHidden ? (
                                        // The image is filtered out of view, so
                                        // the sets name it rather than point up.
                                        <FormattedMessage
                                            id="patientList.drilldown.contouredOnNamed"
                                            values={{
                                                name: (
                                                    <SensitiveInfoTypography>
                                                        {seriesDisplayName(
                                                            intl,
                                                            node.series
                                                        )}
                                                    </SensitiveInfoTypography>
                                                ),
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage id="patientList.drilldown.contouredOnThisImage" />
                                    )}
                                </Typography>
                                {node.structureSets.map((set, setIndex) => (
                                    <Box key={rowKey(set, group.day, setIndex)}>
                                        {row(set, group.day, setIndex)}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );

    /** One dataset row in whichever mode this group is rendering. */
    function row(series: SeriesDto, day: string, index: number) {
        const key = rowKey(series, day, index);
        // A nested set lists under its image's day; its own date shows when it differs.
        const ownDay = formatDay(series.seriesDateTime);
        const infoProps = {
            infoKey: key,
            info,
            infoOpen: infoOpenKey === key,
            infoPinned: infoPinnedKey === key,
            ownDate:
                ownDay && ownDay !== day
                    ? formatDayText(intl, ownDay)
                    : undefined,
        };
        if (patientRows !== undefined) {
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control="patient"
                    primary={patientRows.primary}
                    onTick={patientRows.onTick}
                    ticked={
                        series.id !== undefined &&
                        series.id !== null &&
                        (patientRows.ticked?.has(series.id) ?? false)
                    }
                    {...infoProps}
                />
            );
        }
        // Selection is keyed by a real series id; id-less series are not selectable.
        const seriesId = seriesKey(series);
        if (selection === undefined || seriesId === undefined) {
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control={props.mode === 'imported' ? 'imported' : 'none'}
                    {...infoProps}
                />
            );
        }
        if (selection.alreadyStored?.has(seriesId)) {
            return (
                <DatasetRow
                    key={key}
                    series={series}
                    control="alreadyStored"
                    {...infoProps}
                />
            );
        }
        return (
            <DatasetRow
                key={key}
                series={series}
                control="select"
                seriesId={seriesId}
                checked={selection.checked.has(seriesId)}
                onToggle={selection.onToggle}
                {...infoProps}
            />
        );
    }
}

const BUBBLE_SIZE = 12;
const RAIL_WIDTH = 2;
const RAIL_COLOR = 'text.disabled';
/** Header padding (px: 3) plus half a bubble, less half the rail, so the rail runs through the bubbles. */
const RAIL_LEFT = 24 + BUBBLE_SIZE / 2 - RAIL_WIDTH / 2;

const rowKey = (series: SeriesDto, day: string, index: number): string =>
    seriesKey(series) ?? `${day}-${index}`;
